/**
 * Runtime brand switch (DMI pilot).
 *
 * The server declares the active brand via the PUBLIC GET /api/brand endpoint
 * (driven by the BRAND env var in docker-compose.prod.yml). The client applies
 * it as `<html data-brand="…">`, which scss/_dmi-brand.scss keys every DMI
 * override on. Ordering, for a flash-free first paint:
 *
 *   1. `?brand=dmi|default` query override — instant preview without a
 *      redeploy; persists to localStorage so navigation keeps it.
 *   2. localStorage cache — applied synchronously before first render.
 *   3. GET /api/brand — the authoritative value; result re-applied + cached.
 *
 * Called from index.jsx before ReactDOM renders anything.
 */

import api from './services/api.js';

const STORAGE_KEY = 'brand';
// A ?brand= preview pins the brand for this browser until ?brand=reset —
// without the pin, the server-truth fetch below would revert the preview on
// the very next page load (found live on the first walkthrough).
const OVERRIDE_KEY = 'brandOverride';
// DMI ships a light/dark THEME dimension on top of the brand (the style
// guide mandates both; scss/_dmi-brand.scss carries a dark layer keyed on
// `<html data-ui-theme="dark">`). Persisted per browser; light is default.
const THEME_KEY = 'dmiTheme';
const VALID_BRANDS = ['default', 'dmi'];

// Favicons per brand. The default pair is what index.html declares; under DMI
// the tab shows the DMI mark (the light-surface variant reads on a browser tab).
const FAVICONS = {
  default: { svg: '/favicon.svg', type: 'image/svg+xml' },
  dmi: { svg: '/brand/dmi/dmi-logo-main.png', type: 'image/png' },
};

function applyFavicon(brand) {
  const icon = FAVICONS[brand] || FAVICONS.default;
  const link = document.querySelector('link[rel="icon"]');
  if (link) {
    link.href = icon.svg;
    link.type = icon.type;
  }
  const touch = document.querySelector('link[rel="apple-touch-icon"]');
  if (touch) touch.href = icon.svg;
}

function applyPersistedUiTheme(brand) {
  // The theme dimension exists only under the DMI brand; any other brand
  // drops the attribute so the dark layer can never leak across brands.
  let theme = null;
  if (brand === 'dmi') {
    try {
      theme = localStorage.getItem(THEME_KEY);
    } catch {
      /* storage unavailable — default to light */
    }
  }
  // DMI defaults to the DARK variant until the light theme's page sweep is
  // complete (owner call, 2026-09-01): no stored choice means dark; an
  // explicit stored 'light' wins.
  if (brand === 'dmi' ? theme !== 'light' : false) {
    document.documentElement.dataset.uiTheme = 'dark';
  } else {
    delete document.documentElement.dataset.uiTheme;
  }
}

function applyBrand(brand) {
  if (!VALID_BRANDS.includes(brand)) return;
  document.documentElement.dataset.brand = brand;
  applyFavicon(brand);
  applyPersistedUiTheme(brand);
  try {
    localStorage.setItem(STORAGE_KEY, brand);
  } catch {
    /* storage may be unavailable (private mode) — the brand still applies */
  }
}

/** The DMI theme active RIGHT NOW ('light' unless the dark layer is on). */
export function currentUiTheme() {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.dataset.uiTheme === 'dark' ? 'dark' : 'light';
}

/**
 * Switch the DMI light/dark theme (navbar sun/moon toggle). Sets
 * `<html data-ui-theme="dark">` for dark, removes the attribute for light,
 * and persists the choice to localStorage so reloads keep it.
 */
export function setUiTheme(theme) {
  const dark = theme === 'dark';
  if (dark) {
    document.documentElement.dataset.uiTheme = 'dark';
  } else {
    delete document.documentElement.dataset.uiTheme;
  }
  try {
    localStorage.setItem(THEME_KEY, dark ? 'dark' : 'light');
  } catch {
    /* storage unavailable — the theme still applies for this page */
  }
}

/**
 * The brand active RIGHT NOW. Read per render (never cached at module scope):
 * a `?brand=` navigation can re-apply the attribute after modules loaded, and
 * initBrand() runs before React renders, so a render-time read is always
 * current.
 */
export function currentBrand() {
  if (typeof document === 'undefined') return 'default';
  return document.documentElement.dataset.brand || 'default';
}

export function initBrand() {
  // 1. Query override — wins outright and is persisted for the session.
  let fromQuery = null;
  try {
    fromQuery = new URLSearchParams(window.location.search).get('brand');
  } catch {
    /* ignore malformed URLs */
  }
  if (fromQuery === 'reset') {
    try {
      localStorage.removeItem(OVERRIDE_KEY);
    } catch {
      /* ignore */
    }
  } else if (fromQuery && VALID_BRANDS.includes(fromQuery)) {
    applyBrand(fromQuery);
    try {
      localStorage.setItem(OVERRIDE_KEY, fromQuery);
    } catch {
      /* ignore */
    }
    return;
  }

  // 1b. A pinned preview outlives its originating page load; server truth
  // resumes only after ?brand=reset.
  let override = null;
  try {
    override = localStorage.getItem(OVERRIDE_KEY);
  } catch {
    /* ignore */
  }
  if (override && VALID_BRANDS.includes(override)) {
    applyBrand(override);
    return;
  }

  // 2. Cached value — synchronous, so the first paint is already branded.
  let cached = null;
  try {
    cached = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  if (cached && VALID_BRANDS.includes(cached)) applyBrand(cached);

  // 3. Server truth — public endpoint, so this works on the sign-in page too
  // (api.request simply omits the Authorization header when no token exists).
  api
    .request('/brand')
    .then((data) => {
      if (data && VALID_BRANDS.includes(data.brand)) applyBrand(data.brand);
    })
    .catch(() => {
      /* offline / API down: keep whatever was cached; never block the app */
    });
}
